import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Input } from "../../controls";
import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { safeOnComplete } from "../../utils/safeOnComplete";
import PrintReceipt from "./PrintReceipt";

import {
  printReceiptHtmlDirect,
  buildReceiptHtml,
  getPrintAgentStatus,
} from "../../api/actions/printAgent";

const PrintSaleReceipt = (props) => {
  const {
    _get,
    _store,
    receiptData,
    setReceiptData,
    setAlert,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCLoader } = useContext(AppContext);

  const [invoiceId, setInvoiceId] = useState("");
  const [saleLoaded, setSaleLoaded] = useState(false);

  const loadSale = () => {
        if (!invoiceId) {
            setAlert({
            isOpen: true,
            type: "warning",
            title: t("warning"),
            subTitle: t("invoiceIdRequired"),
            });
            return;
        }

        setCLoader(true);

        const onComplete = safeOnComplete(
            (res) => {
            setCLoader(false);

            if (
                res.status === 200 &&
                res.result &&
                Number(res.result.storeId) === Number(_store.id)
            ) {
                setReceiptData(res.result);
                setSaleLoaded(true);
            } else {
                setSaleLoaded(false);

                setAlert({
                isOpen: true,
                type: "warning",
                title: t("warning"),
                subTitle: t("invoiceNotFound"),
                });
            }
            },
            (err) => {
            setCLoader(false);
            setSaleLoaded(false);

            setAlert({
                isOpen: true,
                type: "error",
                title: t("error"),
                subTitle:
                err?.msg || t("invoiceNotFound"),
            });
            }
        );

        _get(
            endpoints.Sell +
            controller.GetById +
            "/" +
            invoiceId,
            null,
            onComplete
        );
    };

    const printSale = async () => {
        if (!saleLoaded) return;

        const agentStatus = await getPrintAgentStatus();

        if (!agentStatus.running) {
            setAlert({
            isOpen: true,
            type: "warning",
            title: t("printAgentNotRunning"),
            subTitle: t("printAgentNotRunningMessage"),
            });

            return;
        }

        if (!_store?.id) {
            setAlert({
            isOpen: true,
            type: "warning",
            title: t("printer"),
            subTitle: t("noStoreSelected"),
            });

            return;
        }

        setCLoader(true);

        const printerType = 1;

        const onPrinterComplete = safeOnComplete(
            (printerRes) => {
            if (
                printerRes.status !== 200 ||
                !printerRes.result
            ) {
                setCLoader(false);

                setAlert({
                isOpen: true,
                type: "warning",
                title: t("printer"),
                subTitle: t("noDefaultReceiptPrinter"),
                });

                return;
            }

            const printer = printerRes.result;

            const receiptElement =
                document.getElementById("printSellReceipt");

            if (!receiptElement) {
                setCLoader(false);

                setAlert({
                isOpen: true,
                type: "error",
                title: t("error"),
                subTitle: t("receiptNotPrinted"),
                });

                return;
            }

            const receiptHtml = buildReceiptHtml(
                receiptElement,
                printer.paperWidth || 80
            );

            printReceiptHtmlDirect(
                printer.printerName,
                receiptHtml,
                printer.paperWidth || 80
            )
                .then(() => {
                setCLoader(false);

                setAlert({
                    isOpen: true,
                    type: "success",
                    title: t("successful"),
                    subTitle: t("receiptPrinted"),
                });
                })
                .catch((err) => {
                console.error(
                    "HTML DIRECT PRINT ERROR:",
                    err
                );

                setCLoader(false);

                setAlert({
                    isOpen: true,
                    type: "warning",
                    title: t("receiptNotPrinted"),
                    subTitle: t(
                    "receiptNotPrintedMessage"
                    ),
                });
                });
            },
            (err) => {
            setCLoader(false);

            setAlert({
                isOpen: true,
                type: "warning",
                title: t("printer"),
                subTitle:
                err?.msg || t("noDefaultReceiptPrinter"),
            });
            }
        );

        _get(
            endpoints.StorePrinter +
            controller.GetDefaultPrinter +
            `/${_store.id}/${printerType}`,
            null,
            onPrinterComplete
        );
        };

return (
    <>
        <Grid className="gray-color mb-20 p-5">
        <Input
            value={invoiceId}
            onChange={(e) => {
            setInvoiceId(e.target.value);

            // Hide previous receipt when invoice number changes
            setSaleLoaded(false);
            setReceiptData({});
            }}
            type="number"
            variant="outlined"
            className="bg-white rmv-arrow br-8 w-100pr"
            InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                <Typography className="fw-6 fs-20">
                    {t("invoiceId")}:
                </Typography>
                </InputAdornment>
            ),
            }}
            sx={{
            "& .MuiInputBase-input": {
                fontSize: "28px",
                fontWeight: "600",
                color: "var(--gray-color)",
            },
            }}
        />

        {/* Load receipt */}
        <Button
            onClick={loadSale}
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
        >
            {t("loadReceipt")}
        </Button>

        {/* Receipt preview */}
        {saleLoaded && (
            <Box
            sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff",
                borderRadius: "8px",
                maxHeight: "500px",
                overflow: "auto",
            }}
            >
            <PrintReceipt data={receiptData} />

            <Box
                sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                }}
            >
                <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                    setSaleLoaded(false);
                    setReceiptData({});
                }}
                >
                {t("cancel")}
                </Button>

                <Button
                variant="contained"
                fullWidth
                onClick={printSale}
                >
                {t("printReceipt")}
                </Button>
            </Box>
            </Box>
        )}

        {/* Return to POS */}
        <Button
            onClick={() => {
                setReceiptData({});
                navigate("/pos");
            }}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
        >
            {t("back")}
        </Button>
        </Grid>

        {!saleLoaded && (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
            {t("enterInvoiceToPrint")}
            </Typography>
        </Box>
        )}
    </>
    );        
  
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _get: actions.get,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(PrintSaleReceipt);